import { useState, useRef, useEffect, useCallback, type ReactNode } from 'react';

interface InfoTooltipProps {
  children: ReactNode;
  title: string;
  description: string;
  position?: 'top' | 'bottom';
  delay?: number;
}

export default function InfoTooltip({ children, title, description, position = 'top', delay = 2000 }: InfoTooltipProps) {
  const [show, setShow] = useState(false);
  const [coords, setCoords] = useState<{ top: boolean }>({ top: position === 'top' });
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = useCallback(() => {
    timerRef.current = setTimeout(() => setShow(true), delay);
  }, [delay]);

  const handleMouseLeave = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setShow(false);
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!show || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceAbove = rect.top;
    const spaceBelow = window.innerHeight - rect.bottom;
    setCoords({ top: position === 'top' ? spaceAbove > 100 : spaceBelow < 100 });
  }, [show, position]);

  return (
    <div
      ref={triggerRef}
      className="relative h-full"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      <div
        ref={tooltipRef}
        className={`absolute z-50 pointer-events-none transition-all duration-300 ease-out ${
          show ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        } ${coords.top ? 'bottom-full mb-3' : 'top-full mt-3'} left-1/2 -translate-x-1/2`}
        style={{ width: 'max-content', maxWidth: '280px' }}
      >
        <div
          className="relative rounded-2xl px-4 py-3 overflow-hidden"
          style={{
            background: `
              radial-gradient(ellipse 80% 50% at 25% 0%, rgba(255,255,255,0.08) 0%, transparent 60%),
              radial-gradient(ellipse 50% 40% at 75% 5%, rgba(200,230,255,0.05) 0%, transparent 50%),
              linear-gradient(168deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.04) 35%, rgba(255,255,255,0.015) 100%)
            `,
            backdropFilter: 'blur(40px) saturate(1.6) brightness(1.05)',
            WebkitBackdropFilter: 'blur(40px) saturate(1.6) brightness(1.05)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderTopColor: 'rgba(255,255,255,0.25)',
            boxShadow: `
              0 8px 32px rgba(0,0,0,0.3),
              0 2px 8px rgba(0,0,0,0.2),
              inset 0 1px 0 rgba(255,255,255,0.15),
              inset 0 -1px 0 rgba(255,255,255,0.04)
            `,
          }}
        >
          <div
            className="absolute top-0 left-[5%] w-[55%] h-px"
            style={{
              background: 'linear-gradient(90deg, transparent 0%, rgba(200,230,255,0.2) 15%, rgba(255,255,255,0.25) 50%, rgba(200,230,255,0.15) 85%, transparent 100%)',
            }}
          />
          <p className="text-xs font-semibold text-white mb-0.5">{title}</p>
          <p className="text-[11px] text-white/70 leading-relaxed">{description}</p>
        </div>
        <div
          className={`absolute left-1/2 -translate-x-1/2 w-2.5 h-2.5 rotate-45 ${
            coords.top ? '-bottom-[5px]' : '-top-[5px]'
          }`}
          style={{
            background: 'linear-gradient(168deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)',
            backdropFilter: 'blur(40px) saturate(1.6)',
            WebkitBackdropFilter: 'blur(40px) saturate(1.6)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderTop: coords.top ? undefined : 'none',
            borderLeft: coords.top ? undefined : 'none',
            borderBottom: coords.top ? 'none' : undefined,
            borderRight: coords.top ? 'none' : undefined,
          }}
        />
      </div>
    </div>
  );
}
